import { GameState, GameEvent } from '@/types/career-sim';
import { gameEvents } from '@/components/gamification/career-sim/gameData';

export const shouldTriggerEvent = (gameState: GameState, lastActionId?: string): boolean => {
  // Don't trigger events too frequently
  if (gameState.actionCount % 3 !== 0) return false;

  // Random 30% chance
  if (Math.random() < 0.3) return true;

  // Stat-triggered events
  if (gameState.needs.energy < 20) return true;
  if (gameState.needs.mentalHealth < 30) return true;
  if (gameState.career.funds < 100) return true;
  
  // Milestone-triggered events
  if (gameState.career.fanbase === 100 || gameState.career.fanbase === 500 || gameState.career.fanbase === 1000) {
    return true;
  }

  // Action-triggered events
  if (lastActionId === 'perform-live' && Math.random() < 0.2) return true;
  if (lastActionId === 'social-media' && Math.random() < 0.15) return true;

  return false;
};

export const selectEvent = (gameState: GameState, lastActionId?: string): GameEvent | null => {
  // Filter events based on game state - for now, just filter out seen events
  // In the future, we can add requirements to GameEvent type or use metadata
  const availableEvents = gameEvents.filter(event => {
    // Check if already seen
    if (gameState.seenEvents.includes(event.id)) return false;

    return true;
  });

  if (availableEvents.length === 0) return null;

  // Prioritize by type and context
  let selectedEvent: GameEvent | null = null;

  // Check for stat-specific events first
  if (gameState.needs.energy < 20) {
    selectedEvent = availableEvents.find(e => e.id === 'burnout-warning') || null;
  } else if (gameState.needs.mentalHealth < 30) {
    selectedEvent = availableEvents.find(e => e.id === 'mental-health-crisis') || null;
  } else if (gameState.career.funds < 100) {
    selectedEvent = availableEvents.find(e => e.id === 'broke') || null;
  }

  // Check for milestone events
  if (!selectedEvent) {
    if (gameState.career.fanbase === 100) {
      selectedEvent = availableEvents.find(e => e.id === 'first-hundred-fans') || null;
    } else if (gameState.career.fanbase === 1000) {
      selectedEvent = availableEvents.find(e => e.id === 'thousand-fans') || null;
    }
  }

  // Check for action-triggered events
  if (!selectedEvent && lastActionId) {
    const actionEvents = availableEvents.filter(e => 
      e.id.includes(lastActionId.split('-')[0])
    );
    if (actionEvents.length > 0) {
      selectedEvent = actionEvents[Math.floor(Math.random() * actionEvents.length)];
    }
  }

  // Random selection from available events
  if (!selectedEvent) {
    selectedEvent = availableEvents[Math.floor(Math.random() * availableEvents.length)];
  }

  return selectedEvent;
};

export const applyEventOutcome = (
  gameState: GameState,
  outcome: any
): Partial<GameState> => {
  const updates: Partial<GameState> = {
    seenEvents: [...gameState.seenEvents, outcome.eventId || '']
  };

  // Apply stat changes
  if (outcome.funds !== undefined) {
    updates.career = {
      ...gameState.career,
      funds: gameState.career.funds + outcome.funds
    };
  }

  if (outcome.fanbase !== undefined) {
    updates.career = {
      ...updates.career || gameState.career,
      fanbase: gameState.career.fanbase + outcome.fanbase
    };
  }

  if (outcome.reputation !== undefined) {
    updates.career = {
      ...updates.career || gameState.career,
      reputation: gameState.career.reputation + outcome.reputation
    };
  }

  const updatedNeeds = { ...gameState.needs };
  if (outcome.mentalHealth !== undefined) {
    updatedNeeds.mentalHealth = Math.max(0, Math.min(100, updatedNeeds.mentalHealth + outcome.mentalHealth));
  }
  if (outcome.energy !== undefined) {
    updatedNeeds.energy = Math.max(0, Math.min(100, updatedNeeds.energy + outcome.energy));
  }
  if (outcome.creativity !== undefined) {
    updatedNeeds.creativity = Math.max(0, Math.min(100, updatedNeeds.creativity + outcome.creativity));
  }
  updates.needs = updatedNeeds;

  // Apply unlocks
  if (outcome.unlocks && outcome.unlocks.length > 0) {
    updates.unlockedActions = [...gameState.unlockedActions, ...outcome.unlocks];
  }

  return updates;
};
